import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BookOpen, Menu, X } from "lucide-react";
import LanguageSelector from "../../../components/LanguageSelector";
import UserMenu from "../../../components/UserMenu";
import UsageIndicator from "../../../components/UsageIndicator";
import { AuthContext } from "../../auth/context/AuthContext";

function SearchHeader({ onToggleSidebar, showSidebar, manualUrl = "" }) {
  const { user } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
      <div className="mx-auto px-2 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Mobile menu button - only shows on mobile */}
          {user && (
            <button
              onClick={onToggleSidebar}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors mr-2"
              aria-label="Toggle sidebar"
            >
              {showSidebar ? (
                <X className="h-5 w-5 text-gray-600" />
              ) : (
                <Menu className="h-5 w-5 text-gray-600" />
              )}
            </button>
          )}

          <button
            onClick={() => navigate("/")}
            className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1 cursor-pointer hover:opacity-90 transition-opacity"
            aria-label="Go to main search"
          >
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-white rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md ring-1 ring-gray-100">
              <div className="w-7 h-7 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg sm:rounded-xl flex items-center justify-center">
                <img
                  src="/logo.svg"
                  alt="MediLens"
                  className="h-5 w-5 sm:h-7 sm:w-7"
                />
              </div>
            </div>
            <div className="min-w-0 flex-1 text-left">
              <h1 className="text-base sm:text-2xl font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 bg-clip-text text-transparent tracking-wide font-sans animate-gradient bg-[length:200%_auto]">
                MediLens
              </h1>
              <p className="hidden sm:block text-xs sm:text-sm text-gray-500 mt-0.5 truncate">
                {t("app.subtitle")}
              </p>
            </div>
          </button>

          {/* Right side - Usage indicator, Language selector and User menu */}
          <div className="flex items-center space-x-1 sm:space-x-3 flex-shrink-0 ml-2">
            {manualUrl ? (
              <a
                href={manualUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-2.5 py-2 text-xs font-medium text-blue-700 transition-colors hover:border-blue-200 hover:bg-blue-100 hover:text-blue-800 sm:px-3"
                aria-label={t("app.manual")}
              >
                <BookOpen className="h-4 w-4" />
                <span className="hidden sm:inline">{t("app.manual")}</span>
              </a>
            ) : null}

            {/* Show usage indicator for authenticated users - hide on mobile */}
            {user && (
              <div className="hidden sm:block">
                <UsageIndicator userRole={user.role} />
              </div>
            )}

            <LanguageSelector />

            {/* Show user menu only if user is authenticated */}
            {user && <UserMenu />}
          </div>
        </div>
      </div>
    </header>
  );
}

export default SearchHeader;
